// The pieces of a line's note, as the line editor's note shortcuts read and
// write them (UC-3.R8, #915). A piece is the text between two «·», trimmed;
// an empty piece is no piece. The order store counts the shortcuts by the
// same split (domain.NotePieces in internal/core/domain/note_shortcut.go),
// and both tests read internal/core/domain/testdata/note_pieces.json, so a
// shortcut is on for exactly the notes it was counted from. Which words to
// offer is the store's answer; this only edits the note's text.

/** A note's length, MaxNoteLength in internal/core/usecase/uc03_customize_line.go. */
export const MAX_NOTE_LENGTH = 200;

/** What joins two pieces of a note: the editor writes « · ». */
export const NOTE_SEPARATOR = '·';

/** The rows of note shortcuts the editor shows under the note (T6). */
export const NOTE_SHORTCUT_ROWS = 2;

// Go's strings.TrimSpace, which the core trims a piece with: Unicode's white
// space, U+0085 and U+00A0 included, and no other character.
const space =
	'[\\t\\n\\v\\f\\r \\u0085\\u00A0\\u1680\\u2000-\\u200A\\u2028\\u2029\\u202F\\u205F\\u3000]+';
const edges = new RegExp(`^${space}|${space}$`, 'g');

function trimmed(piece: string): string {
	return piece.replace(edges, '');
}

/** A note's pieces, in its order, each once. */
export function notePieces(note: string): string[] {
	const out: string[] = [];
	for (const raw of note.split(NOTE_SEPARATOR)) {
		const piece = trimmed(raw);
		if (piece !== '' && !out.includes(piece)) out.push(piece);
	}
	return out;
}

/** Whether the note holds the words as one whole piece, not inside a longer one. */
export function holdsPiece(note: string, words: string): boolean {
	return notePieces(note).includes(trimmed(words));
}

/**
 * A tap on a shortcut: the words join the note as one more piece, or, while
 * the note holds them, that piece goes. The note is written back as its
 * pieces (notePieces) joined by « · ». A piece that would not fit whole in
 * the note's length is not added: half a word would print, and be counted.
 */
export function togglePiece(note: string, words: string): string {
	const piece = trimmed(words);
	const pieces = notePieces(note);
	const next = pieces.includes(piece) ? pieces.filter((p) => p !== piece) : [...pieces, piece];
	const joined = next.join(` ${NOTE_SEPARATOR} `);
	return Array.from(joined).length > MAX_NOTE_LENGTH ? note : joined;
}

/**
 * How many chips, laid out in order, lie in their first `rows` rows: the
 * chips' tops are given in order, and a chip whose top starts a row past the
 * last one is the first hidden, with every chip after it.
 */
export function fitCount(rowTops: number[], rows: number): number {
	let row = 0;
	let top = Number.NEGATIVE_INFINITY;
	for (const [i, at] of rowTops.entries()) {
		// A pixel either way is the same row: tops are rounded.
		if (at > top + 1) {
			row++;
			top = at;
		}
		if (row > rows) return i;
	}
	return rowTops.length;
}
